import { TemperatureUnit } from '../types/Global';
import { MapNumberToFanMode, MapNumberToThermostatWorkingMode, ThermostatWorkingMode, ScreenStatus, } from '../types/HvacControl';
import { isTimetableCurrentlyActive } from './timeTables';
import { createTemperatureValue } from './temperature';
import { isDefined, pluralize } from './util';
import global from './global';
import { addSeconds, differenceInMinutes, fromUnixTime } from 'date-fns/esm';
import { ScreenLockLabel, Vendor } from '../types';
import { getUserPreferenceTemperatureUnit } from './temperature';
import { ReversingValve, HeatingSytem } from '../types/Gateway';
import { getHvacTypeLabelFromHvacType } from './gateways';
export const renderLocalAdjustmentDegree = (subscribedThermostat, currentUser) => {
    var _a, _b, _c;
    if ((subscribedThermostat === null || subscribedThermostat === void 0 ? void 0 : subscribedThermostat.desiredSettings) === null) {
        return null;
    }
    const LOCAL_ADJUSTMENT_ISSUE_MESSAGE = 'Local Adjustment issue';
    const desiredThermostatWorkingMode = (_a = subscribedThermostat.desiredSettings) === null || _a === void 0 ? void 0 : _a.mode;
    const currentThermostatWorkingMode = subscribedThermostat.mode;
    if (desiredThermostatWorkingMode !== currentThermostatWorkingMode) {
        return LOCAL_ADJUSTMENT_ISSUE_MESSAGE;
    }
    const desiredCoolSetPoint = (_b = subscribedThermostat.desiredSettings) === null || _b === void 0 ? void 0 : _b.coolSetPoint;
    const currentCoolSetPoint = subscribedThermostat.coolSetPoint;
    const desiredHeatSetPoint = (_c = subscribedThermostat.desiredSettings) === null || _c === void 0 ? void 0 : _c.heatSetPoint;
    const currentHeatSetPoint = subscribedThermostat.heatSetPoint;
    const coolSetPointDelta = currentCoolSetPoint && desiredCoolSetPoint
        ? createTemperatureValue(currentCoolSetPoint, currentUser, 0) -
            createTemperatureValue(desiredCoolSetPoint, currentUser, 0)
        : null;
    const heatSetPointDelta = currentHeatSetPoint && desiredHeatSetPoint
        ? createTemperatureValue(currentHeatSetPoint, currentUser, 0) -
            createTemperatureValue(desiredHeatSetPoint, currentUser, 0)
        : null;
    switch (currentThermostatWorkingMode) {
        case ThermostatWorkingMode.AUTO: {
            const areDeltasEqual = coolSetPointDelta === heatSetPointDelta;
            if (!areDeltasEqual)
                return LOCAL_ADJUSTMENT_ISSUE_MESSAGE;
            if (!coolSetPointDelta && !heatSetPointDelta) {
                return null;
            }
            return renderLocalAdjustmentStatement(coolSetPointDelta);
        }
        case ThermostatWorkingMode.HEATING: {
            if (!heatSetPointDelta)
                return null;
            return renderLocalAdjustmentStatement(heatSetPointDelta);
        }
        case ThermostatWorkingMode.COOLING: {
            if (!coolSetPointDelta)
                return null;
            return renderLocalAdjustmentStatement(coolSetPointDelta);
        }
        default: {
            return null;
        }
    }
};
/**
 * @param setPointDelta can be in Celsius/Fahrenheit, no conversion takes place
 */
export const renderLocalAdjustmentStatement = (setPointDelta) => {
    //we purposefully return null if setPointDelta is 0
    if (!setPointDelta) {
        return null;
    }
    const setPointDeltaWithPlusOrMinusSign = setPointDelta > 0 ? `+${setPointDelta}` : `${setPointDelta}`;
    return `Local Adjustment ${setPointDeltaWithPlusOrMinusSign} ${pluralize('degree', setPointDelta)}`;
};
// TODO: delete this after all edgeApp updates
export const renderSopTempDeviation = (configuredSetPoint, currentSetPoint, currentUser, timetable, timetableSetPoint, siteTz) => {
    const temperatureUnit = getUserPreferenceTemperatureUnit(currentUser);
    if (!isDefined(configuredSetPoint) || !isDefined(currentSetPoint)) {
        return;
    }
    if (!isDefined(timetableSetPoint)) {
        return;
    }
    // should we be comparing the regular or out of schedule set point?
    let relevantSopSetPoint = configuredSetPoint;
    if (!isTimetableCurrentlyActive(timetable, siteTz) &&
        isDefined(timetableSetPoint)) {
        relevantSopSetPoint = timetableSetPoint;
    }
    let deviation = currentSetPoint - relevantSopSetPoint;
    if (temperatureUnit === TemperatureUnit.F) {
        deviation = (deviation * 9) / 5;
    }
    // Negative sign displays automatically, but explicitly show a + otherwise
    const positiveSign = deviation > 0 ? '+' : '';
    if (deviation !== 0) {
        return `Local Adjustment of ${positiveSign}${deviation} degrees`;
    }
};
const normalizeDotList = (dotList, vendor) => {
    // Check if the vendor is "energybox" before excluding "00:00:00:00:00"
    if (vendor === Vendor.ENERGYBOX) {
        return dotList === null || dotList === void 0 ? void 0 : dotList.filter((item) => item.uuid !== '00:00:00:00:00:00').map((item) => item.uuid);
    }
    else {
        // If the vendor is not "energybox", return the original dotList
        return dotList === null || dotList === void 0 ? void 0 : dotList.map((item) => item.uuid);
    }
};
export const normalizeSubscribedThermostat = (data) => ({
    additionalTemperatureReadings: data.additionalTemperatureReadings,
    coolSetPoint: data.coolSetPoint,
    coolingStage1Running: data.coolingStage1Running,
    coolingStage2Running: data.coolingStage2Running,
    fanMode: MapNumberToFanMode[data.fanMode] || null,
    fanRunning: data.fanRunning,
    heatSetPoint: data.heatSetPoint,
    heatingStage1Running: data.heatingStage1Running,
    heatingStage2Running: data.heatingStage2Running,
    id: Number(data.id),
    localAdjustmentOverride: data.localAdjustmentOverride,
    localAdjustmentOverrideEnabled: data.localAdjustmentOverrideEnabled,
    localAdjustmentOverrideEnergybox: data.localAdjustmentOverrideEnergybox,
    mode: MapNumberToThermostatWorkingMode[data.mode] || null,
    online: data.online,
    overrideTime: data.overrideTime,
    readingType: data.readingType,
    tempUnits: data.tempUnits,
    temperature: data.temperature,
    timestamp: data.timestamp,
    uuid: data.uuid,
    vendor: data.vendor,
    workingMode: data.workingMode,
    desiredSettings: isDefined(data.desiredSettings)
        ? normalizeDesiredSettings(data.desiredSettings)
        : null,
    screenLocked: data.screenLocked,
    screenStatus: data.screenStatus,
    timetableActiveState: data.timetableActiveState,
    timetableActiveStateEndTime: data.timetableActiveStateEndTime,
    timetableActiveStateStartTime: data.timetableActiveStateStartTime,
});
const normalizeRFTstatDotList = (dotList) => {
    // exclude "00:00:00:00:00"
    return dotList === null || dotList === void 0 ? void 0 : dotList.filter((item) => item !== '00:00:00:00:00:00').map((item) => item);
};
export const normalizeSubscribedRFThermostatStatus = (data) => {
    var _a;
    return ({
        coolSetPoint: data.coolSetPoint,
        coolingStage1Running: data.temperature_state === 'FIRST_STAGE_COOLING',
        coolingStage2Running: data.temperature_state === 'SECOND_STAGE_COOLING',
        fanMode: null,
        fanRunning: data.fan_state === 'FAN_ON',
        heatSetPoint: data.heatSetPoint,
        heatingStage1Running: data.temperature_state === 'FIRST_STAGE_HEATING',
        heatingStage2Running: data.temperature_state === 'SECOND_STAGE_HEATING',
        id: Number(data.id),
        mode: null,
        tempUnits: data.configuration.display_unit,
        temperature: data.configuration.temperature,
        timestamp: data.timestamp,
        uuid: data.uuid,
        vendor: data.vendor,
        dotList: normalizeRFTstatDotList(data.dot_list),
        humidity: data.configuration.humidity,
        humidityMode: data.configuration.humidity_mode,
        humidityState: data.humidity_state,
        humiditySetPoint: data.configuration.humidify_set_point,
        dehumidifySetPoint: data.configuration.dehumidify_set_point,
        deadbandStage1: data.configuration.deadband_stage_1,
        deadbandStage2: data.configuration.deadband_stage_2,
        compressorProtectionTime: data.configuration.compressor_protection_time,
        temperatureSensorFallbackSequence: data.configuration.temperature_sensor_fallback,
        temperatureSensorSource: data.configuration.temperature_sensor_source,
        temperatureSensorFallbackTime: data.configuration.sensor_fallback_time,
        temperatureSensorFallback: data.configuration.sensor_fallback,
        hvacType: getHvacTypeLabelFromHvacType(data.configuration.system_type),
        reversingValve: data.configuration.valve_logic === 'NORMAL'
            ? ReversingValve.REVERSING_VALVE_O
            : ReversingValve.REVERSING_VALVE_B,
        heatingSystem: data.configuration.gas_system
            ? HeatingSytem.GAS
            : HeatingSytem.ELECTRIC,
        adjustmentLimit: (_a = data.configuration) === null || _a === void 0 ? void 0 : _a.adjustment_limit,
        temperatureSensorStatus: data.temperature_sensor_status,
    });
};
export const normalizeDesiredSettings = (data) => ({
    coolSetPoint: data.coolSetPoint,
    fanMode: MapNumberToFanMode[data.fanMode] || null,
    heatSetPoint: data.heatSetPoint,
    localAdjustmentOverrideEnabled: data.localAdjustmentOverrideEnabled,
    mode: MapNumberToThermostatWorkingMode[data.mode] || null,
    screenLocked: data.screenLocked,
    screenStatus: data.screenStatus,
    setpointSecurityMax: data.setpointSecurityMax,
    setpointSecurityMin: data.setpointSecurityMin,
    tempUnits: data.tempUnits,
});
export const renderLocalSetpointDeltaSign = (thermostatWorkingMode) => {
    if (thermostatWorkingMode === ThermostatWorkingMode.COOLING) {
        return '-';
    }
    else if (thermostatWorkingMode === ThermostatWorkingMode.HEATING) {
        return '+';
    }
    else if (thermostatWorkingMode === ThermostatWorkingMode.AUTO) {
        return '+/-';
    }
    else {
        return ''; // shouldn't happen with current typing
    }
};
export const renderLocalAdjustmentTitle = (thermostatWorkingMode, setPointLimitDelta) => {
    if (!isDefined(setPointLimitDelta)) {
        return global.NOT_AVAILABLE;
    }
    return `${renderLocalSetpointDeltaSign(thermostatWorkingMode)}${setPointLimitDelta}  ${pluralize('degree', setPointLimitDelta)}`;
};
export const renderOverrideTime = (overrideTime, overrideTimerLengthInSec, isUnixTimestamp = true // Default value set to true
) => {
    if (!overrideTime || overrideTime === 0)
        return global.NOT_AVAILABLE;
    let numMinsLeftOverride = 0;
    if (isUnixTimestamp) {
        const now = new Date();
        const overrideDate = fromUnixTime(Math.floor(overrideTime / 1000));
        const revertDate = addSeconds(overrideDate, overrideTimerLengthInSec);
        numMinsLeftOverride = Math.abs(differenceInMinutes(now, revertDate));
    }
    else {
        numMinsLeftOverride = overrideTime;
    }
    const numHours = Math.floor(numMinsLeftOverride / 60);
    const numMinutes = numMinsLeftOverride % 60;
    return `${numHours}:${numMinutes < 10 ? '0' : ''}${numMinutes}`;
};
const isScreenLocked = (subscribedThermostat) => {
    // The check for screenLocked can be removed when all Edge devices are updated to the
    // newest version (1.15.1+) of the edge-app. We will then only need to rely on screenStatus.
    if (subscribedThermostat.screenLocked === true) {
        return true;
    }
    if (subscribedThermostat.screenStatus === ScreenStatus.UNKNOWN &&
        subscribedThermostat.screenLocked === false) {
        return undefined;
    }
    return subscribedThermostat.screenStatus === ScreenStatus.LOCKED;
};
export const showThermostatDisplay = (subscribedThermostat) => {
    return isDefined(isScreenLocked(subscribedThermostat))
        ? isScreenLocked(subscribedThermostat)
        : false;
};
export const getThermostatDisplayLabel = (subscribedThermostat) => {
    return isDefined(isScreenLocked(subscribedThermostat))
        ? ScreenLockLabel[String(showThermostatDisplay(subscribedThermostat))]
        : global.NOT_AVAILABLE;
};
